import React, { useState, useEffect } from "react";
import { AppBar, Toolbar, Typography, Modal, Box, Link, CssBaseline, FormControlLabel, Checkbox, IconButton } from "@mui/material";
import { HelpCenter, Close, Settings } from "@mui/icons-material";
import PokemonSelectPage from "../PokemonSelectPage";
import { otherSetting, WEATHER_EMPTY, BATTLE_FIELD_EMPTY } from "../OtherSetting";
import { storageClient, STORAGE_KEYS } from "../storage";

const defaultOtherSetting: otherSetting = {
  weather: WEATHER_EMPTY,
  battle_field: BATTLE_FIELD_EMPTY,
  battleStatuses: [],
};

export default function App() {
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [currentOtherSetting, setCurrentOtherSetting] = useState<otherSetting>(defaultOtherSetting);
  const [showSvOnly, setShowSvOnly] = useState(true);

  useEffect(() => {
    const loadResult = storageClient.local.getItem<boolean>(STORAGE_KEYS.POKEMON_SELECT_SHOW_SV_ONLY);
    if (loadResult.success && typeof loadResult.data === 'boolean') {
      setShowSvOnly(loadResult.data);
      return;
    }
    // 読み込み失敗や未設定の場合はON扱い
    setShowSvOnly(true);
  }, []);

  const handleShowSvOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.checked;
    setShowSvOnly(nextValue);
    const saveResult = storageClient.local.setItem(STORAGE_KEYS.POKEMON_SELECT_SHOW_SV_ONLY, nextValue);
    if (!saveResult.success) {
      console.error('ポケモンSVのみ表示の保存に失敗しました:', saveResult.error);
    }
  };


  // 外部から保存/読み込みを呼び出すための中継
  const handleSave = () => {
    if ((window as any).pokemonSelectSave) {
      (window as any).pokemonSelectSave();
    }
  };

  const handleLoad = () => {
    if ((window as any).pokemonSelectLoad) {
      (window as any).pokemonSelectLoad();
    }
  };

  const handleToggleRightSidebar = () => {
    setRightSidebarOpen(!rightSidebarOpen);
  };


  const handleOpenHelpModal = () => {
    setHelpModalOpen(true);
  };

  const handleCloseHelpModal = () => {
    setHelpModalOpen(false);
  };

  const handleOpenSettingsModal = () => {
    setSettingsModalOpen(true);
  };

  const handleCloseSettingsModal = () => {
    setSettingsModalOpen(false);
  };
  
  return (
    <>
      <CssBaseline />
      <Box sx={{ 
        width: '100vw', 
        height: '100vh', 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <AppBar position="static" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, flexShrink: 0 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            まとめて能力調整(仮)
            <Box 
              component="button"
              onClick={handleOpenHelpModal}
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'inherit',
                ml: 1,
                p: 0.5,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .help-text': {
                    textDecoration: 'underline'
                  }
                }
              }}
            >
              <HelpCenter sx={{ fontSize: '1.2rem' }} />
              <Typography 
                variant="body2" 
                className="help-text"
                sx={{ 
                  ml: 0.5,
                  fontSize: '0.875rem'
                }}
              >
                (ヘルプ/フィードバック)
              </Typography>
            </Box>
            <Box
              component="button"
              onClick={handleOpenSettingsModal}
              type="button"
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'inherit',
                ml: 1,
                p: 0.5,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .settings-text': {
                    textDecoration: 'underline'
                  }
                }
              }}
            >
              <Settings sx={{ fontSize: '1.2rem' }} />
              <Typography
                variant="body2"
                className="settings-text"
                sx={{
                  ml: 0.5,
                  fontSize: '0.875rem'
                }}
              >
                (設定)
              </Typography>
            </Box>
          </Typography>
        </Toolbar>
        </AppBar>
        
        <Box sx={{ 
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <PokemonSelectPage 
            onSave={handleSave} 
            onLoad={handleLoad}
            rightSidebarOpen={rightSidebarOpen}
            onToggleRightSidebar={handleToggleRightSidebar}
            currentOtherSetting={currentOtherSetting}
            onOtherSettingChange={setCurrentOtherSetting}
            showSvOnly={showSvOnly}
          />
        </Box>
        
        {/* Help Modal */}
        <Modal
          open={helpModalOpen}
          onClose={handleCloseHelpModal}
          aria-labelledby="help-modal-title"
          aria-describedby="help-modal-description"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography id="help-modal-title" variant="h6" component="h2">
                ヘルプ
              </Typography>
              <IconButton aria-label="閉じる" onClick={handleCloseHelpModal} size="small">
                <Close />
              </IconButton>
            </Box>
            <Typography id="help-modal-description" sx={{ mb: 2 }}>
              検算には
              <Link 
                href="https://sv.pokesol.com/calc" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ textDecoration: 'underline' }}
              >
                ポケモンソルジャー様のダメージ計算ツール
              </Link>
              を使用しております。
            </Typography>
            <Typography sx={{ mt: 2 }}>
              <Link 
                href="https://lit.link/fvrchan"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'underline' }}
              >
                その他リンクはこちら
              </Link>
            </Typography>
          </Box>
        </Modal>

        {/* Settings Modal */}
        <Modal
          open={settingsModalOpen}
          onClose={handleCloseSettingsModal}
          aria-labelledby="settings-modal-title"
          aria-describedby="settings-modal-description"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography id="settings-modal-title" variant="h6" component="h2">
                設定
              </Typography>
              <IconButton aria-label="閉じる" onClick={handleCloseSettingsModal} size="small">
                <Close />
              </IconButton>
            </Box>
            <Typography id="settings-modal-description" sx={{ mb: 2 }}>
              表示に関する設定
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showSvOnly}
                  onChange={handleShowSvOnlyChange}
                />
              }
              label="ポケモンSVのみを表示"
            />
          </Box>
        </Modal>
      </Box>
    </>
  );
}
